import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import LanguageSelector from "../common/LanguageSelector";
import ThemeToggle from "../common/ThemeToggle";

/**
 * Layout pour les utilisateurs connectés
 * Interface privée avec design moderne et fonctionnalités optimisées
 */
const UserLayout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Récupérer l'état de la sidebar depuis localStorage
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Sauvegarder l'état de la sidebar
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Fermer le menu mobile lors du changement de route
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Gérer la touche Escape pour fermer le menu mobile
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  // Bloquer le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  /**
   * Gérer la déconnexion
   */
  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("sidebarCollapsed");
    if (onLogout) {
      onLogout(null, null);
    }
    navigate("/");
  }, [navigate, onLogout]);

  /**
   * Navigation optimisée
   */
  const handleNavigation = useCallback(
    (path) => {
      navigate(path);
      setIsMobileMenuOpen(false);
    },
    [navigate]
  );

  // Icônes SVG professionnelles
  const getIcon = (iconName) => {
    const icons = {
      dashboard: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
      community: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      profile: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      recipes: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      fridge: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      ),
      worldMenus: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      favorites: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
      shoppingList: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.25 3h1.386c.51 0 .955.343 1.087.836l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 1.994-4.708 2.602-7.201.245-1.005-.52-1.999-1.55-1.999H5.106M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
          />
        </svg>
      ),
    };
    return icons[iconName];
  };

  // Éléments de navigation avec mémoisation — un seul accent de marque
  // (ember) pour l'état actif plutôt qu'une couleur différente par item :
  // cohérent avec la palette restreinte du site public.
  const navItems = useMemo(
    () => [
      {
        id: "dashboard",
        label: t("home") || "Accueil",
        icon: "dashboard",
        path: "/dashboard",
      },
      {
        id: "community",
        label: t("community") || "Communauté",
        icon: "community",
        path: "/community",
      },
      {
        id: "profile",
        label: t("profile") || "Profil",
        icon: "profile",
        path: "/profile",
      },
      {
        id: "recipes",
        label: t("recipes") || "Recettes",
        icon: "recipes",
        path: "/recipes",
      },
      {
        id: "shopping-list",
        label:
          t("shoppingListTitle") !== "shoppingListTitle"
            ? t("shoppingListTitle")
            : "Liste de Courses",
        icon: "shoppingList",
        path: "/shopping-list",
      },
      {
        id: "fridge-assistant",
        label: t("fridgeAssistant") || "Vide-Frigo",
        icon: "fridge",
        path: "/fridge-assistant",
      },
      {
        id: "world-menus",
        label: t("worldMenus") || "Menus du Monde",
        icon: "worldMenus",
        path: "/world-menus",
      },
      {
        id: "favorites",
        label: t("favorites") || "Favoris",
        icon: "favorites",
        path: "/favorites",
      },
    ],
    [t]
  );

  // Vérifier si un élément de navigation est actif
  const isActive = useCallback(
    (itemPath) => {
      return location.pathname === itemPath;
    },
    [location.pathname]
  );

  return (
    <div className="flex h-screen bg-ink-50 dark:bg-ink-900 relative overflow-hidden">
      {/* Sidebar moderne et élégante */}
      <aside
        className={`hidden lg:flex bg-white dark:bg-ink-900 transition-all duration-300 ease-out ${
          isCollapsed ? "w-20" : "w-80"
        } h-screen flex-col shadow-xl shadow-ink-900/10 dark:shadow-ink-900/50 min-w-20 relative z-10 border-r border-ink-900/10 dark:border-ink-700`}
      >
        {/* Bordure décorative top : accent de marque, plus un dégradé arc-en-ciel */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-ember-600"></div>

        {/* Header clean et moderne */}
        <div className="relative p-6 border-b border-ink-900/10 dark:border-ink-700 mt-1">
          <div className="flex items-center justify-between">
            {!isCollapsed && (
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <img
                    src="/logojes.png"
                    alt="JesCook Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-ink-900 dark:text-white">
                    JesCook
                  </h1>
                  <p className="text-xs text-ink-500 dark:text-ink-400 font-medium">
                    {t("personalSpace") || "Dashboard"}
                  </p>
                </div>
              </div>
            )}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2.5 rounded-xl hover:bg-ember-50 dark:hover:bg-ink-800 transition-all duration-200 group"
              aria-label={
                isCollapsed ? "Ouvrir la sidebar" : "Fermer la sidebar"
              }
              aria-expanded={!isCollapsed}
            >
              <svg
                className={`w-5 h-5 text-ink-400 group-hover:text-ember-600 transition-all duration-200 ${
                  isCollapsed ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation élégante */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const itemIsActive = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full group relative flex items-center space-x-4 px-4 py-3 rounded-xl transition-all duration-200 ${
                  itemIsActive
                    ? "bg-ember-600 text-white shadow-md"
                    : "text-ink-600 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-800 hover:text-ink-900 dark:hover:text-white"
                }`}
                aria-current={itemIsActive ? "page" : undefined}
              >
                <span className="flex-shrink-0">{getIcon(item.icon)}</span>
                {!isCollapsed && (
                  <>
                    <span
                      className={`text-base flex-1 text-left truncate ${
                        itemIsActive ? "font-semibold" : "font-medium"
                      }`}
                    >
                      {item.label}
                    </span>
                    {itemIsActive && (
                      <svg
                        className="w-5 h-5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* Section utilisateur */}
        {user && (
          <div className="p-5 border-t border-ink-900/10 dark:border-ink-700">
            {!isCollapsed ? (
              <div className="bg-ink-50 dark:bg-ink-800 rounded-2xl p-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-ember-400 to-ember-600 text-white rounded-2xl flex items-center justify-center text-base font-bold shadow-md">
                    {user.first_name
                      ? user.first_name.charAt(0).toUpperCase()
                      : user.username.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-ink-900 dark:text-white truncate">
                      {user.first_name || user.username}
                    </p>
                    <p className="text-xs text-ink-500 dark:text-ink-400 truncate">
                      {user.email}
                    </p>
                  </div>
                </div>

                {/* Thème Toggle */}
                <div className="mb-3">
                  <ThemeToggle className="w-full shadow-sm" />
                </div>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-white dark:bg-ink-700 hover:bg-ink-100 dark:hover:bg-ink-600 text-ink-700 dark:text-ink-200 rounded-xl text-sm font-medium transition-all duration-200 border border-ink-900/10 dark:border-ink-600 shadow-sm"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>{t("logout") || "Déconnexion"}</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Thème Toggle (collapsed) */}
                <ThemeToggle className="w-full" />

                <button
                  onClick={handleLogout}
                  className="w-full p-3 hover:bg-ember-50 dark:hover:bg-ink-800 rounded-xl transition-all duration-200 flex items-center justify-center"
                  title={t("logout") || "Déconnexion"}
                >
                  <svg
                    className="w-6 h-6 text-ink-600 dark:text-ink-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Contenu principal */}
      <div className="flex-1 flex flex-col overflow-hidden relative z-0">
        {/* Header premium */}
        <header className="bg-white/95 dark:bg-ink-900/95 backdrop-blur-sm sticky top-0 z-40 shadow-sm">
          <div className="px-6 py-3.5 border-b border-ink-900/10 dark:border-ink-700">
            <div className="flex items-center justify-between">
              {/* Menu mobile */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-xl hover:bg-ember-50 dark:hover:bg-ink-800 transition-colors duration-200"
                aria-label={
                  isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"
                }
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <svg
                  className="w-6 h-6 text-ink-700 dark:text-ink-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={
                      isMobileMenuOpen
                        ? "M6 18L18 6M6 6l12 12"
                        : "M4 6h16M4 12h16M4 18h16"
                    }
                  />
                </svg>
              </button>

              {/* Breadcrumb et titre */}
              <div className="flex-1 flex items-center space-x-4">
                <div className="hidden lg:flex items-center space-x-2 text-sm">
                  <div className="text-ember-600">
                    {getIcon(
                      navItems.find((item) => isActive(item.path))?.icon ||
                        "dashboard"
                    )}
                  </div>
                  <span className="text-ink-400">/</span>
                  <span className="font-semibold text-ink-900 dark:text-white">
                    {navItems.find((item) => isActive(item.path))?.label ||
                      t("home") ||
                      "Accueil"}
                  </span>
                </div>

                {/* Info utilisateur sur header */}
                <div className="flex items-center space-x-3 ml-auto lg:ml-0">
                  <div className="hidden md:flex items-center space-x-3 px-4 py-2 bg-ember-50 rounded-xl border border-ember-100">
                    <div className="w-8 h-8 bg-gradient-to-br from-ember-400 to-ember-600 text-white rounded-lg flex items-center justify-center text-xs font-bold">
                      {user?.first_name?.charAt(0).toUpperCase() ||
                        user?.username?.charAt(0).toUpperCase() ||
                        "U"}
                    </div>
                    <div className="hidden xl:block">
                      <p className="text-sm font-semibold text-ink-900">
                        {user?.first_name || user?.username || "Utilisateur"}
                      </p>
                      <p className="text-xs text-ink-500">
                        {user?.email || ""}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                {/* Notifications */}
                <button className="hidden sm:flex p-2.5 rounded-xl hover:bg-ink-50 dark:hover:bg-ink-800 transition-colors relative">
                  <svg
                    className="w-5 h-5 text-ink-600 dark:text-ink-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>

                <LanguageSelector />

                <ThemeToggle />

                {user && (
                  <button
                    onClick={handleLogout}
                    className="hidden sm:flex items-center space-x-2 px-4 py-2.5 bg-ink-900 dark:bg-ink-700 hover:bg-ink-800 dark:hover:bg-ink-600 text-white rounded-xl text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span>{t("logout") || "Déconnexion"}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Ligne de progression décorative : accent de marque uni */}
          <div className="h-0.5 bg-ember-600"></div>
        </header>

        {/* Menu mobile */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/40 z-50"
            onClick={() => setIsMobileMenuOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navigation"
          >
            <div
              id="mobile-menu"
              className="absolute left-0 top-0 bottom-0 w-80 bg-white dark:bg-ink-900 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Bordure décorative top : accent de marque, plus un dégradé arc-en-ciel */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-ember-600"></div>

              {/* Header mobile */}
              <div className="relative p-6 border-b border-ink-900/10 dark:border-ink-700 mt-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                      <img
                        src="/logojes.png"
                        alt="JesCook Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-ink-900 dark:text-white">
                        JesCook
                      </h1>
                      <p className="text-xs text-ink-500 font-medium">
                        {t("personalSpace") || "Dashboard"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2.5 rounded-xl hover:bg-ember-50 transition-all duration-200 group"
                  >
                    <svg
                      className="w-5 h-5 text-ink-400 group-hover:text-ember-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Navigation mobile */}
              <nav className="relative px-4 py-6 space-y-2 overflow-y-auto max-h-[calc(100vh-240px)]">
                {navItems.map((item) => {
                  const itemIsActive = isActive(item.path);
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full group relative flex items-center space-x-4 px-4 py-3 rounded-xl transition-all duration-200 ${
                        itemIsActive
                          ? "bg-ember-600 text-white shadow-md"
                          : "text-ink-600 hover:bg-ink-50 hover:text-ink-900"
                      }`}
                      aria-current={itemIsActive ? "page" : undefined}
                    >
                      <span className="flex-shrink-0">
                        {getIcon(item.icon)}
                      </span>
                      <span
                        className={`text-base flex-1 text-left truncate ${
                          itemIsActive ? "font-semibold" : "font-medium"
                        }`}
                      >
                        {item.label}
                      </span>
                      {itemIsActive && (
                        <svg
                          className="w-5 h-5 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </nav>

              {/* User info mobile */}
              {user && (
                <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-ink-900/10">
                  <div className="bg-ink-50 rounded-2xl p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-ember-400 to-ember-600 text-white rounded-2xl flex items-center justify-center text-base font-bold shadow-md">
                        {user.first_name
                          ? user.first_name.charAt(0).toUpperCase()
                          : user.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-ink-900 truncate">
                          {user.first_name || user.username}
                        </p>
                        <p className="text-xs text-ink-500 truncate">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-white hover:bg-ink-100 text-ink-700 rounded-xl text-sm font-medium transition-all duration-200 border border-ink-900/10 shadow-sm"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      <span>{t("logout") || "Déconnexion"}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Zone de contenu avec design cohérent */}
        {/* Même paire papier/encre que la home publique (VisitorLayout) :
            un vrai fond clair chaud en light, sombre en dark, plutôt qu'un
            fond toujours sombre qui masquait la différence entre les thèmes. */}
        <main
          className={`flex-1 overflow-y-auto ${isDark ? "ink-field" : "paper-field"}`}
        >
          <div className="max-w-[1800px] mx-auto p-4 sm:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default UserLayout;
